import * as React from "react";

import { FullScreenLoader } from "@v2/components/FullScreenLoader";
import { createContext, useRequiredContext } from "replo-utils/react/context";

type AppLoadingContextValue = {
  register: (id: string) => void;
  unregister: (id: string) => void;
};

const AppLoadingContext = createContext<AppLoadingContextValue | null>(
  "AppLoadingContext",
  null,
);

/**
 * Provides a single, shared full-screen loading overlay for the entire app.
 * Multiple loading sources (user auth, project data, etc.) can register
 * themselves, and the loader stays visible until ALL sources finish.
 * This prevents the animation from restarting when loading transitions
 * from one phase to the next (e.g. user auth → project fetch).
 */
export function AppLoadingProvider({
  children,
}: React.PropsWithChildren): React.JSX.Element {
  const [loadingIds, setLoadingIds] = React.useState<Set<string>>(
    () => new Set(),
  );

  const register = React.useCallback((id: string) => {
    setLoadingIds((prev) => new Set(prev).add(id));
  }, []);

  const unregister = React.useCallback((id: string) => {
    setLoadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const value = React.useMemo(
    () => ({ register, unregister }),
    [register, unregister],
  );

  const isVisible = loadingIds.size > 0;

  return (
    <AppLoadingContext.Provider value={value}>
      {/* Always mounted so Lottie pre-warms its assets before it's first needed */}
      <FullScreenLoader visible={isVisible} />
      {children}
    </AppLoadingContext.Provider>
  );
}

/**
 * Registers a named loading source with the AppLoadingProvider.
 * While any source is loading, the shared full-screen loader stays visible.
 *
 * Uses useLayoutEffect to synchronize register/unregister within the same
 * React commit, preventing any visual flicker between loading phases.
 */
export function useAppLoading(id: string, isLoading: boolean) {
  const { register, unregister } = useRequiredContext(AppLoadingContext);

  React.useLayoutEffect(() => {
    if (isLoading) {
      register(id);
    } else {
      unregister(id);
    }
    return () => {
      unregister(id);
    };
  }, [register, unregister, id, isLoading]);
}
